// Deployment-skew recovery for Next.js Server Actions.
//
// Every storefront build re-hashes Server Action IDs. A browser — or a CDN edge
// entry served via stale-while-revalidate — still holding a page from a PREVIOUS
// build POSTs the OLD action id when the shopper clicks a button wired to a
// server action. The running build has no such id and throws "Failed to find
// Server Action" (UnrecognizedActionError), so the click silently does nothing
// (this is exactly what made "Add to bag" fail for returning shoppers after the
// bKash-driven redeploys on 2026-07-20). Purging the CDN cannot reach a tab that
// is already open or a page the browser cached locally; the only cure is to
// reload that tab onto the current build.
//
// This module is the single source of truth for detecting that failure and
// reloading ONCE per session (a sessionStorage guard prevents a reload loop if
// the reload itself lands on another stale page). It is imported both by the
// global window error listener (uncaught paths) and by the individual client
// components whose try/catch would otherwise swallow the error before it ever
// reached that listener — the cart/checkout mutations are precisely those caught
// paths, which is why the global listener alone was not enough.

const STALE_ACTION_RELOAD_KEY = "alif_stale_action_reloaded"

export function isStaleServerActionError(error: unknown): boolean {
  if (!error) return false
  if (typeof error === "string") {
    return (
      error.includes("Failed to find Server Action") ||
      error.includes("was not found on the server")
    )
  }
  if (typeof error !== "object") return false
  const e = error as Record<string, unknown>
  const message = typeof e.message === "string" ? e.message : ""
  const name = typeof e.name === "string" ? e.name : ""
  return (
    name === "UnrecognizedActionError" ||
    message.includes("Failed to find Server Action") ||
    message.includes("was not found on the server")
  )
}

// One-shot, loop-guarded full reload so a stale tab picks up the current build.
// Returns true if it actually triggered a reload, false if it was suppressed
// (already reloaded this session, or no sessionStorage to guard the loop).
export function reloadForStaleDeploy(): boolean {
  if (typeof window === "undefined") return false
  try {
    if (window.sessionStorage.getItem(STALE_ACTION_RELOAD_KEY)) {
      // Already reloaded once this session — a second stale hit is not a
      // transient skew a reload can fix; let the caller show its normal error.
      return false
    }
    window.sessionStorage.setItem(STALE_ACTION_RELOAD_KEY, String(Date.now()))
  } catch {
    return false // no sessionStorage → can't guard the loop, so don't reload
  }
  window.location.reload()
  return true
}

// Drop-in for a catch block: if `error` is a stale-deployment skew, trigger a
// one-shot reload and return true so the caller can bail out of its normal error
// handling (the page is about to navigate). Returns false for every other error,
// so the caller proceeds exactly as before.
//
//   } catch (error) {
//     if (recoverFromStaleDeploy(error)) return
//     ...existing handling...
//   }
export function recoverFromStaleDeploy(error: unknown): boolean {
  if (!isStaleServerActionError(error)) return false
  return reloadForStaleDeploy()
}
